import React, { useRef, useState } from 'lib/react';
import {
  EVButton,
  EVGrid, EVKeyboardArrowDown, makeStyles,
} from 'lib/components';
import { useHistory, useLocation } from 'lib/router';
import PropTypes from 'prop-types';
import { useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import { REPORT_DETAIL_ROUTE } from 'constants/RouteConstants';
import ReportFilesDropdown from './ReportFilesDropdown.component';

export const useStyles = makeStyles((theme) => ({
  detailsButtonContainer: {
    width: '125px',
  },
  detailsBtn: {
    padding: '4px 12px 4px 12px',
    flex: 1,
    '&.squareEdge': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  filesDropdownBtn: {
    minWidth: 'unset',
    padding: '4px 8px 4px 8px',
    borderLeft: `1px solid ${theme.evPalette.salt[100]}`,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    '&.open': {
      backgroundColor: theme.evPalette.ocean[800],
      '& .myev-MuiButton-label': { transform: 'rotateX(180deg)' },
    },
  },
  detailsBtnLabel: {
    padding: 0,
    alignItems: 'center',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '19px',
    textTransform: 'none',
  },
}));

function DetailsButton({ reportId, primaryProductID, showFilesEsxUpgrade = false }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const reportFileTypes = useSelector(({ orderHistory: { fileTypes } }) => fileTypes?.[reportId]);
  const [openFilesDropdown, setOpenFilesDropdown] = useState(false);
  const detailsButtonContainerRef = useRef();
  const currentUrl = `${location.pathname}${location.search}`;

  const hasFilesList = (reportFileTypes?.length ?? 0) > 0;
  const showFilesDropdown = hasFilesList || showFilesEsxUpgrade;

  const handleFilesDropdownState = () => setOpenFilesDropdown((open) => !open);

  return (
    <EVGrid container wrap="nowrap" className={classes.detailsButtonContainer} ref={detailsButtonContainerRef}>
      <EVButton
        id={`detailsBtn-${reportId}`}
        aria-label={`detailsBtn-${reportId}`}
        variant="contained"
        color="primary"
        className={clsx(classes.detailsBtn, showFilesDropdown && 'squareEdge')}
        classes={{ label: classes.detailsBtnLabel }}
        onClick={() => { history.push(REPORT_DETAIL_ROUTE.replace(':reportId', reportId), { currentUrl }); }}
      >
        {t('order_history__actions__details')}
      </EVButton>
      {showFilesDropdown && (
        <EVButton
          id={`filesDropdownBtn-${reportId}`}
          aria-label={`filesDropdownBtn-${reportId}`}
          variant="contained"
          color="primary"
          className={clsx(classes.filesDropdownBtn, openFilesDropdown && 'open')}
          onClick={handleFilesDropdownState}
        >
          <EVKeyboardArrowDown />
        </EVButton>
      )}

      <ReportFilesDropdown
        key={openFilesDropdown}
        reportId={reportId}
        handleClickAway={handleFilesDropdownState}
        open={openFilesDropdown}
        anchorEl={detailsButtonContainerRef.current}
        primaryProductID={primaryProductID}
        showFilesEsxUpgrade={showFilesEsxUpgrade}
      />
    </EVGrid>
  );
}

DetailsButton.defaultProps = {
  showFilesEsxUpgrade: false,
};

DetailsButton.propTypes = {
  reportId: PropTypes.number.isRequired,
  primaryProductID: PropTypes.number.isRequired,
  showFilesEsxUpgrade: PropTypes.bool,
};

export default DetailsButton;
